import { useEffect } from 'react';

import {
  Avatar,
  Box,
  DialogContent,
  Drawer,
  makeStyles,
  Typography,
  useTheme,
} from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import dayjs from 'dayjs';

import DrawerBackHeader from 'components/DrawerBackHeader';
import EmptyState from 'components/EmptyState';
import { useAppDispatch, useAppSelector } from 'store';
import {
  fetchOpportunityHistory,
  funnelsSelector,
} from 'store/reducers/funnels';

const useStyles = makeStyles(({ appDrawer, spacing }) => ({
  drawer: {
    width: appDrawer.minWidth,
  },
  drawerPaper: {
    width: appDrawer.minWidth,
    flexShrink: 0,
    overflow: 'hidden',
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing(3),
  },
  divider: {
    margin: spacing(1, -3),
  },
}));

type OpportunityHistoryDrawerProps = {
  open: boolean;
  onClose: () => void;
  funnelId: string;
  opportunityId: string;
  contactName?: string;
  opportunityCode?: string;
};

function OpportunityHistoryDrawer(props: OpportunityHistoryDrawerProps) {
  const {
    open,
    onClose,
    funnelId,
    opportunityId,
    contactName,
    opportunityCode,
  } = props;
  const theme = useTheme();
  const classes = useStyles();
  const dispatch = useAppDispatch();

  const { opportunityHistory, loadingOpportunityHistory } =
    useAppSelector(funnelsSelector);

  useEffect(() => {
    if (open) {
      dispatch(fetchOpportunityHistory({ funnelId, opportunityId }));
    }
  }, [dispatch, funnelId, open, opportunityId]);

  return (
    <Drawer
      transitionDuration={0}
      open={open}
      onClose={onClose}
      className={classes.drawer}
      classes={{ paper: classes.drawerPaper }}
      anchor="right"
      ModalProps={{ hideBackdrop: true }}
    >
      <DrawerBackHeader
        title="Histórico de etapas"
        description={`${contactName} - Cód. da oportunidade: ${opportunityCode}`}
        onBack={onClose}
      />

      <DialogContent className={classes.content}>
        {loadingOpportunityHistory && (
          <Box display="flex" flexDirection="column" style={{ gap: 16 }}>
            {Array.from(new Array(5)).map((_, index) => (
              <Box
                // eslint-disable-next-line react/no-array-index-key
                key={index}
                style={{
                  paddingBottom: 16,
                  borderBottom:
                    index === 5 ? 'none' : `1px solid ${theme.palette.divider}`,
                }}
              >
                <Box
                  style={{ display: 'flex', flexDirection: 'column', gap: 4 }}
                >
                  <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                    <Skeleton
                      style={{ borderRadius: 24 }}
                      variant="rect"
                      width={32}
                      height={32}
                    />
                    <Skeleton width={56} height={20} />
                  </Box>

                  <Skeleton height={20} />

                  <Skeleton width={120} height={20} />
                </Box>
              </Box>
            ))}
          </Box>
        )}

        {!loadingOpportunityHistory && opportunityHistory.length > 0 && (
          <Box
            style={{
              display: 'flex',
              gap: 16,
              marginTop: 16,
              flexDirection: 'column',
            }}
          >
            {opportunityHistory.map((item, index) => (
              <Box
                key={item.id}
                style={{
                  paddingBottom: 16,
                  borderBottom:
                    index === opportunityHistory.length - 1
                      ? 'none'
                      : `1px solid ${theme.palette.divider}`,
                }}
              >
                <Box display="flex" flexDirection="column" style={{ gap: 4 }}>
                  <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                    <Avatar
                      style={{ width: 32, height: 32 }}
                      src={item.actor?.avatarUrl}
                    />

                    <Typography variant="body2" color="textSecondary">
                      {item.actor?.name}
                    </Typography>
                  </Box>

                  {item.fromStage && item.toStage && (
                    <Typography variant="body2">
                      Alterou a etapa de{' '}
                      <Box component="strong">{item.fromStage?.name}</Box> para{' '}
                      <Box component="strong">{item.toStage?.name}</Box>.
                    </Typography>
                  )}

                  {item.toStage && !item.fromStage && (
                    <Typography variant="body2">
                      Oportunidade criada na etapa{' '}
                      <Box component="strong">{item.toStage.name}</Box>.
                    </Typography>
                  )}

                  <Typography color="textSecondary" variant="caption">
                    {dayjs(item.enteredAt).fromNow()}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        )}

        {!loadingOpportunityHistory && opportunityHistory.length === 0 && (
          <EmptyState title="Nenhum registro encontrado" />
        )}
      </DialogContent>
    </Drawer>
  );
}

export default OpportunityHistoryDrawer;
